import type { UpdateSiteSettingsInput } from '@repo/api-contracts';
import { useState } from 'react';
import { useInvalidateSiteSettings } from '@/entities/site/setting';
import { trpc } from '@/shared/api';
import { getErrorMessage } from '@/shared/lib';

export interface UseUpdateSiteSettingsResult {
  /** 성공하면(설정을 다시 읽은 뒤) true. 서버가 거부하면 false 이고 문구는 error 에 남는다. */
  readonly update: (input: UpdateSiteSettingsInput) => Promise<boolean>;
  /** 요청과 그 뒤 설정 재조회가 끝날 때까지 true. */
  readonly isPending: boolean;
  readonly error: string | null;
}

const FORM_ERROR_FALLBACK = '카카오톡 채널 주소를 저장하지 못했습니다.';

/** 운영 설정 저장(siteSettings.update) — 고객용 웹은 다음 조회부터 새 주소를 쓴다. 감사는 서버가 남긴다. */
export const useUpdateSiteSettings = (): UseUpdateSiteSettingsResult => {
  const [error, setError] = useState<string | null>(null);
  const settings = useInvalidateSiteSettings();
  const mutation = trpc.siteSettings.update.useMutation({
    // 재조회 promise 를 돌려준다 — 라이브러리가 그것을 기다려, 새 값이 올 때까지 isPending 이 남는다.
    onSuccess: () => settings.invalidate(),
  });

  const update = async (input: UpdateSiteSettingsInput): Promise<boolean> => {
    setError(null);
    try {
      await mutation.mutateAsync(input);
      return true;
    } catch (cause) {
      setError(getErrorMessage(cause, FORM_ERROR_FALLBACK));
      return false;
    }
  };

  return { update, isPending: mutation.isPending, error };
};
